import { Link } from 'react-router-dom';

import { useMyPendingAccountRequestsCount } from '@/lib/accountRequests';
import { type MeResponse } from '@/lib/auth';

/**
 * Header pill that shows the pending account-request count for users
 * who can act on it. Renders nothing when there are zero pending —
 * the goal is "you have something to do" signal, not a permanent
 * ornament.
 *
 * Click destination depends on the user's privilege:
 * - Site admin → /admin (the full queue)
 * - Org admin only → first org they admin where pending requests live
 *   (we don't know which org has them from the count alone, so we
 *   pick the first admin'd org's settings page where the org-scoped
 *   panel will surface the request)
 *
 * The count endpoint works for any authenticated user; the response
 * is zero when they have no scope to act on, which collapses the
 * pill to nothing.
 */
export function AdminBadge({ me }: { me: MeResponse }) {
  const pending = useMyPendingAccountRequestsCount();
  const count = pending.data?.count ?? 0;
  if (count === 0) return null;

  const isSiteAdmin =
    me.user.is_superuser || me.system_roles.includes('admin');

  // For org-admin-only users, route to the first org where they have
  // owner/admin role. The org's panel will list its pending requests.
  const firstAdminOrgSlug = !isSiteAdmin
    ? me.organizations.find(
        (m) => m.role === 'owner' || m.role === 'admin',
      )?.slug
    : undefined;
  const target = isSiteAdmin
    ? '/admin'
    : firstAdminOrgSlug
      ? `/orgs/${firstAdminOrgSlug}`
      : '/';

  return (
    <Link
      to={target}
      title={`${count} pending account request${count === 1 ? '' : 's'}`}
      className="rounded-full bg-forge-100 dark:bg-forge-900 px-2 py-0.5 text-xs font-medium text-forge-700 dark:text-forge-300 hover:bg-forge-200 dark:hover:bg-forge-800"
    >
      {count} pending
    </Link>
  );
}
